import { create } from 'zustand';
import { fetchSenderProfile, type SenderProfile } from '@/api/senderProfile';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { getErrorMessage } from '@/lib/error';

interface SenderProfileState {
  open: boolean;
  email: string | null;
  profile: SenderProfile | null;
  loading: boolean;
  error: string | null;
  /** Open the sheet for a sender and load their profile (always unified). */
  openProfile: (email: string) => void;
  close: () => void;
  refresh: () => Promise<void>;
}

export const useSenderProfileStore = create<SenderProfileState>((set, get) => ({
  open: false,
  email: null,
  profile: null,
  loading: false,
  error: null,

  openProfile: (email: string) => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return;
    set({ open: true, email: normalized, profile: null, error: null });
    void get().refresh();
  },

  close: () => set({ open: false }),

  refresh: async () => {
    const { email } = get();
    if (!email) return;
    set({ loading: true, error: null });
    try {
      const profile = await fetchSenderProfile(UNIFIED_ACCOUNT_ID, email);
      // Ignore a stale response if the user opened a different sender meanwhile
      if (get().email === email) {
        set({ profile, loading: false });
      }
    } catch (error) {
      if (get().email === email) {
        set({ error: getErrorMessage(error) ?? 'Failed to load sender profile', loading: false });
      }
    }
  },
}));
